import React, { useState, useEffect, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Spinner,
  Input,
  Label,
  Alert,
} from 'reactstrap';
import { FirebaseContext } from '../../components/Firebase';
import { useAuthStore } from '../../store/auth.store';
import { useOrganizationStore } from '../../store/organization.store';
import WavePlayer from '../../components/WavePlayer';
import * as ROUTES from '../../constants/routes';

interface ReviewInfo {
  review: {
    id: string;
    trackName: string;
    sourceOrgName: string;
    notes?: string;
    status: string;
    expiresAt?: number;
  };
  track: {
    id: string;
    trackName: string;
    mp3Files?: { downloadUrl: string; storageRef: string; fileName: string }[];
    wavFiles?: { downloadUrl: string; storageRef: string; fileName: string }[];
    defaultMp3Id?: string;
    language?: { label: string; value: string }[];
    tags?: { label: string; value: string }[];
    client?: { label: string; value: string };
    agency?: { label: string; value: string };
    studio?: { label: string; value: string };
    /** Spot description / brief — surfaced above the audio player so the
     *  reviewer can confirm the spot matches the brief before listening. */
    notes?: string;
  } | null;
  sourceOrg: {
    id: string;
    name: string;
  } | null;
  trackId: string;
  orgId: string;
  reviewId: string;
}

const ReviewSpot: React.FC = () => {
  const { reviewCode } = useParams<{ reviewCode: string }>();
  const firebase = useContext(FirebaseContext);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewInfo, setReviewInfo] = useState<ReviewInfo | null>(null);
  const [decision, setDecision] = useState<'approved' | 'rejected' | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Batch review state
  const [isBatch, setIsBatch] = useState(false);
  const [batchData, setBatchData] = useState<any>(null);
  const [batchTrackDecisions, setBatchTrackDecisions] = useState<Record<string, { decision: 'approved' | 'rejected'; reason: string }>>({});
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchSubmitted, setBatchSubmitted] = useState(false);
  const [activeTrackIdx, setActiveTrackIdx] = useState<number | null>(null);
  const [rejectingTrackId, setRejectingTrackId] = useState<string | null>(null);
  const [rejectReasonText, setRejectReasonText] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [reviewerEmail, setReviewerEmail] = useState('');

  const history = useHistory();
  const auth = useAuthStore(state => state.auth);
  const isAuthenticated = !!auth;
  const organizations = useOrganizationStore(state => state.organizations);
  const currentOrgId = useOrganizationStore(state => state.currentOrgId);
  const currentOrgName = useOrganizationStore(state => state.currentOrgName);
  const setCurrentOrganization = useOrganizationStore(state => state.setCurrentOrganization);

  // Auto-select org if user has exactly one
  useEffect(() => {
    if (isAuthenticated && organizations.length === 1 && !currentOrgId) {
      const org = organizations[0];
      setCurrentOrganization(org.orgId, org.orgName, org.role, org.orgType || 'artist', org.orgCategory);
    }
  }, [isAuthenticated, organizations, currentOrgId, setCurrentOrganization]);

  // Auth redirect handlers — preserve review code in sessionStorage
  const handleSignIn = () => {
    sessionStorage.setItem('pendingReviewCode', reviewCode || '');
    history.push(ROUTES.SIGN_IN);
  };
  const handleSignUp = () => {
    sessionStorage.setItem('pendingReviewCode', reviewCode || '');
    history.push(ROUTES.SIGN_UP);
  };
  const handleCreateOrg = () => {
    sessionStorage.setItem('pendingReviewCode', reviewCode || '');
    history.push(ROUTES.GET_STARTED);
  };
  const handleSelectOrg = () => {
    sessionStorage.setItem('pendingReviewCode', reviewCode || '');
    history.push(ROUTES.SELECT_ORGANIZATION);
  };

  // Validate the review code on mount
  useEffect(() => {
    const validateCode = async () => {
      if (!firebase || !reviewCode) {
        setError('Invalid review link');
        setLoading(false);
        return;
      }

      try {
        const validation = await firebase.validateReviewCode(reviewCode);

        if (!validation.valid) {
          setError(validation.error || 'Invalid review link');
          setLoading(false);
          return;
        }

        // Check if this is a batch review
        if (validation.reviewInfo?.review?.isBatch || validation.lookupData?.isBatch) {
          const data = validation.lookupData || validation.reviewInfo?.review;
          setIsBatch(true);
          setBatchData(data);
        } else {
          setReviewInfo(validation.reviewInfo as ReviewInfo);
        }
      } catch (err) {
        console.error('Error validating review code:', err);
        setError('Error loading review');
      }
      setLoading(false);
    };

    validateCode();
  }, [firebase, reviewCode]);

  const handleSubmitReview = async () => {
    if (!firebase || !reviewInfo || !decision) return;

    setSubmitting(true);
    try {
      const success = await firebase.submitReview(
        reviewInfo.orgId,
        reviewInfo.trackId,
        reviewInfo.reviewId,
        decision,
        {
          rejectionReason: decision === 'rejected' ? rejectionReason : undefined,
          reviewerName: reviewerName || undefined,
          reviewerEmail: reviewerEmail || undefined,
          reviewedVia: 'external-link',
          reviewerOrgId: currentOrgId || undefined,
          reviewerOrgName: currentOrgName || undefined,
        }
      );

      if (success) {
        setSubmitted(true);
      } else {
        setError('Failed to submit review. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      setError('Error submitting review');
    }
    setSubmitting(false);
  };

  // Resolve a playback URL for the reviewed spot. Prefers the default MP3
  // (if `defaultMp3Id` matches one), then any MP3, then falls back to the
  // first WAV when no MP3 is attached. WAV is natively playable in all
  // modern browsers via HTML5 audio, so WavePlayer handles it fine. Without
  // this fallback, WAV-only spots showed no play button on the review page.
  const getPlaybackUrl = (): string | null => {
    if (!reviewInfo?.track) return null;
    const { mp3Files, wavFiles, defaultMp3Id } = reviewInfo.track;
    if (mp3Files && mp3Files.length > 0) {
      if (defaultMp3Id) {
        const defaultFile = mp3Files.find(f => f.storageRef === defaultMp3Id);
        if (defaultFile?.downloadUrl) return defaultFile.downloadUrl;
      }
      const firstMp3 = mp3Files[0]?.downloadUrl;
      if (firstMp3) return firstMp3;
    }
    if (wavFiles && wavFiles.length > 0) {
      return wavFiles[0]?.downloadUrl || null;
    }
    return null;
  };

  // Shared card styles
  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--chatbot-color-1)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
  };

  const headerStyle: React.CSSProperties = {
    backgroundColor: 'var(--chatbot-color-2)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px 12px 0 0',
    padding: '1.25rem 1.5rem',
  };

  // Batch review: submit all decisions
  const handleBatchSubmit = async () => {
    if (!firebase || !batchData) return;

    const tracks = batchData.tracks || [];
    const decisions = Object.keys(batchTrackDecisions);
    if (decisions.length === 0) return;

    setBatchSubmitting(true);
    for (const track of tracks) {
      const d = batchTrackDecisions[track.trackId];
      if (!d) continue;

      try {
        await firebase.submitReview(
          batchData.orgId,
          track.trackId,
          track.reviewId,
          d.decision,
          {
            rejectionReason: d.decision === 'rejected' ? d.reason : undefined,
            reviewerName: reviewerName || undefined,
            reviewerEmail: reviewerEmail || undefined,
            reviewedVia: 'external-link',
            reviewerOrgId: currentOrgId || undefined,
            reviewerOrgName: currentOrgName || undefined,
          }
        );
      } catch (err) {
        console.error(`Error submitting review for ${track.trackId}:`, err);
      }
    }

    // Update batch lookup status
    try {
      const allDecided = tracks.every((t: any) => batchTrackDecisions[t.trackId]);
      if (allDecided) {
        const { doc: firestoreDoc, updateDoc: firestoreUpdateDoc } = await import('firebase/firestore');
        const lookupRef = firestoreDoc(firebase.db, 'reviewCodes', reviewCode!);
        await firestoreUpdateDoc(lookupRef, { status: 'reviewed' });
      }
    } catch { /* ignore */ }

    setBatchSubmitting(false);
    setBatchSubmitted(true);
  };

  // Batch: approve all tracks at once
  const handleBatchApproveAll = () => {
    if (!batchData?.tracks) return;
    const decisions: Record<string, { decision: 'approved' | 'rejected'; reason: string }> = {};
    for (const track of batchData.tracks) {
      decisions[track.trackId] = { decision: 'approved', reason: '' };
    }
    setBatchTrackDecisions(decisions);
  };

  // Resolve a playback URL for a batch-review track. Mirrors getPlaybackUrl's
  // mp3 → wav fallback so WAV-only spots still get a play button.
  const getBatchTrackPlaybackUrl = (track: any): string | null => {
    if (track.mp3Files && track.mp3Files.length > 0) {
      if (track.defaultMp3Id) {
        const found = track.mp3Files.find((f: any) => f.storageRef === track.defaultMp3Id);
        if (found?.downloadUrl) return found.downloadUrl;
      }
      const firstMp3 = track.mp3Files[0]?.downloadUrl;
      if (firstMp3) return firstMp3;
    }
    if (track.wavFiles && track.wavFiles.length > 0) {
      return track.wavFiles[0]?.downloadUrl || null;
    }
    return null;
  };

  // Loading state
  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner style={{ color: 'var(--accent-1-color)' }} />
      </Container>
    );
  }

  // Error state (also allow batch)
  if (error && !reviewInfo && !batchData) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md="6">
            <Card style={cardStyle}>
              <div style={headerStyle}>
                <h4 style={{ color: '#fff', margin: 0 }}>Review Unavailable</h4>
              </div>
              <CardBody style={{ padding: '1.5rem', color: '#fff' }}>
                <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{error}</p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Batch review page
  if (isBatch && batchData) {
    const tracks = batchData.tracks || [];
    const allDecided = tracks.every((t: any) => batchTrackDecisions[t.trackId]);
    const approvedCount = Object.values(batchTrackDecisions).filter(d => d.decision === 'approved').length;
    const rejectedCount = Object.values(batchTrackDecisions).filter(d => d.decision === 'rejected').length;
    // Action-button gates. Mirrors the single-review path so an authenticated
    // reviewer with at least one org MUST pick one before approving/rejecting —
    // otherwise submitReview gets `reviewerOrgId: undefined` and the spot
    // never gets copied into their org's library.
    const nameRequired = !isAuthenticated && !reviewerName.trim();
    const orgRequired = isAuthenticated && organizations.length > 0 && !currentOrgId;
    const cannotAct = nameRequired || orgRequired;

    if (batchSubmitted) {
      return (
        <Container style={{ maxWidth: '700px', marginTop: '3rem' }}>
          <Card style={cardStyle}>
            <div style={headerStyle}>
              <h4 style={{ color: '#fff', margin: 0 }}>Review Submitted</h4>
            </div>
            <CardBody style={{ padding: '2rem', color: '#fff', textAlign: 'center' }}>
              <i className="tim-icons icon-check-2" style={{ fontSize: '2.5rem', color: '#50c878', display: 'block', marginBottom: '1rem' }} />
              <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                {approvedCount} approved, {rejectedCount} rejected
              </p>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
                The studio has been notified of your decisions.
              </p>
            </CardBody>
          </Card>
        </Container>
      );
    }

    return (
      <Container style={{ maxWidth: '800px', marginTop: '2rem', marginBottom: '3rem' }}>
        {/* Header */}
        <Card style={cardStyle} className="mb-3">
          <div style={headerStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ color: '#fff', margin: 0 }}>{batchData.campaign || 'Spot Review'}</h4>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                  from {batchData.sourceOrgName} &middot; {tracks.length} spot{tracks.length !== 1 ? 's' : ''} to review
                </span>
              </div>
              <Button
                size="sm"
                className="btn-success-custom"
                onClick={handleBatchApproveAll}
                disabled={batchSubmitting || cannotAct}
                title={cannotAct ? (orgRequired ? 'Select an organization first' : 'Enter your name first') : undefined}
              >
                <span style={{ position: 'relative', zIndex: 2 }}>
                  <i className="tim-icons icon-check-2 mr-1"></i>Approve All
                </span>
              </Button>
            </div>
          </div>
          {batchData.notes && (
            <CardBody style={{ padding: '0.75rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', margin: 0, fontStyle: 'italic' }}>
                "{batchData.notes}"
              </p>
            </CardBody>
          )}
        </Card>

        {/* Auth + org-selection card. Mirrors the single-review three-state
            flow so the reviewer's selected org is always explicit before they
            can approve/reject — otherwise the reviewed spot never gets
            copied into their org's library (see submitReview's reviewerOrgId
            branch in Firebase.ts). */}
        <Card style={cardStyle} className="mb-3">
          <CardBody style={{ padding: '1rem' }}>
            {!isAuthenticated ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', flex: 1 }}>
                  <Input
                    bsSize="sm" placeholder="Your name *" value={reviewerName}
                    onChange={(e: any) => setReviewerName(e.target.value)}
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontSize: '0.85rem', flex: 1, minWidth: '120px' }}
                  />
                  <Input
                    bsSize="sm" placeholder="Your email (optional)" value={reviewerEmail}
                    onChange={(e: any) => setReviewerEmail(e.target.value)}
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontSize: '0.85rem', flex: 1, minWidth: '120px' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button size="sm" className="btn-primary-custom" onClick={handleSignIn}>
                    <span style={{ position: 'relative', zIndex: 2 }}>Sign In</span>
                  </Button>
                  <Button size="sm" className="btn-info-custom" onClick={handleSignUp}>
                    <span style={{ position: 'relative', zIndex: 2 }}>Create Account</span>
                  </Button>
                </div>
              </div>
            ) : organizations.length === 0 ? (
              <>
                <Alert color="warning" style={{ marginBottom: '0.5rem' }}>
                  You need an organization to link these reviews to your account.
                </Alert>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                    Create an organization to continue.
                  </span>
                  <Button size="sm" className="btn-primary-custom" onClick={handleCreateOrg}>
                    <span style={{ position: 'relative', zIndex: 2 }}>Create Organization</span>
                  </Button>
                </div>
              </>
            ) : !currentOrgId ? (
              <>
                <Alert color="info" style={{ marginBottom: '0.5rem' }}>
                  Select an organization to link these reviews.
                </Alert>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                    Each approved spot will be added to the selected org's library.
                  </span>
                  <Button size="sm" className="btn-primary-custom" onClick={handleSelectOrg}>
                    <span style={{ position: 'relative', zIndex: 2 }}>Select Organization</span>
                  </Button>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>Reviewing as: </span>
                  <span style={{ color: '#fff', fontWeight: 500 }}>{currentOrgName}</span>
                </div>
                {organizations.length > 1 && (
                  <Button
                    size="sm"
                    color="link"
                    onClick={handleSelectOrg}
                    style={{ color: 'var(--accent-1-color)', fontSize: '0.8rem', padding: 0 }}
                  >
                    Switch
                  </Button>
                )}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Track list */}
        {tracks.map((track: any, idx: number) => {
          const mp3Url = getBatchTrackPlaybackUrl(track);
          const trackDecision = batchTrackDecisions[track.trackId];
          const isActive = activeTrackIdx === idx;
          const isDecided = !!trackDecision;

          return (
            <Card key={track.trackId} style={{
              ...cardStyle,
              marginBottom: '0.75rem',
              borderLeft: isDecided
                ? `3px solid ${trackDecision.decision === 'approved' ? '#50c878' : '#ff5050'}`
                : '3px solid transparent',
            }}>
              <CardBody style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <span style={{ color: '#fff', fontWeight: 500, fontSize: '0.9rem' }}>{track.trackName || 'Untitled'}</span>
                      {isDecided && (
                        <span style={{
                          padding: '1px 6px', borderRadius: '3px', fontSize: '0.65rem',
                          backgroundColor: trackDecision.decision === 'approved' ? 'rgba(80,200,120,0.2)' : 'rgba(255,80,80,0.2)',
                          color: trackDecision.decision === 'approved' ? '#50c878' : '#ff5050',
                        }}>
                          {trackDecision.decision === 'approved' ? 'Approved' : 'Rejected'}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {track.language?.map((l: any) => (
                        <span key={l.value} style={{ padding: '1px 6px', borderRadius: '3px', fontSize: '0.7rem', backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}>
                          {l.label}
                        </span>
                      ))}
                      {track.client && (
                        <span style={{ padding: '1px 6px', borderRadius: '3px', fontSize: '0.7rem', backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}>
                          {typeof track.client === 'object' ? track.client.label : track.client}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {mp3Url && (
                      <button
                        onClick={() => setActiveTrackIdx(isActive ? null : idx)}
                        style={{
                          background: 'none', border: '1px solid rgba(255,255,255,0.2)',
                          color: isActive ? 'var(--accent-1-color)' : 'rgba(255,255,255,0.5)',
                          cursor: 'pointer', fontSize: '0.75rem', padding: '3px 8px', borderRadius: '4px',
                        }}
                      >
                        {isActive ? 'Close' : 'Play'}
                      </button>
                    )}
                    {!isDecided && rejectingTrackId !== track.trackId ? (
                      <>
                        <button
                          onClick={() => setBatchTrackDecisions(prev => ({ ...prev, [track.trackId]: { decision: 'approved', reason: '' } }))}
                          disabled={cannotAct}
                          title={cannotAct ? (orgRequired ? 'Select an organization first' : 'Enter your name first') : undefined}
                          style={{
                            background: 'none', border: '1px solid rgba(80,200,120,0.3)',
                            color: cannotAct ? 'rgba(80,200,120,0.3)' : '#50c878',
                            cursor: cannotAct ? 'not-allowed' : 'pointer',
                            fontSize: '0.75rem', padding: '3px 10px', borderRadius: '4px',
                            opacity: cannotAct ? 0.5 : 1,
                          }}
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => { setRejectingTrackId(track.trackId); setRejectReasonText(''); }}
                          disabled={cannotAct}
                          title={cannotAct ? (orgRequired ? 'Select an organization first' : 'Enter your name first') : undefined}
                          style={{
                            background: 'none', border: '1px solid rgba(255,80,80,0.3)',
                            color: cannotAct ? 'rgba(255,80,80,0.3)' : '#ff5050',
                            cursor: cannotAct ? 'not-allowed' : 'pointer',
                            opacity: cannotAct ? 0.5 : 1,
                            fontSize: '0.75rem',
                            padding: '3px 10px', borderRadius: '4px',
                          }}
                        >
                          Reject
                        </button>
                      </>
                    ) : isDecided ? (
                      <button
                        onClick={() => setBatchTrackDecisions(prev => { const next = { ...prev }; delete next[track.trackId]; return next; })}
                        style={{
                          background: 'none', border: 'none',
                          color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: '0.7rem',
                        }}
                      >
                        Undo
                      </button>
                    ) : null}
                  </div>
                </div>

                {/* Spot description — from audiometa.notes / chase-list brief.
                    Always visible (not gated on isActive) so reviewers see it
                    before pressing Play. */}
                {track.notes && (
                  <div style={{
                    marginTop: '0.6rem',
                    padding: '0.6rem 0.75rem',
                    backgroundColor: 'rgba(255, 180, 50, 0.06)',
                    border: '1px solid rgba(255, 180, 50, 0.2)',
                    borderRadius: '6px',
                  }}>
                    <div style={{ color: 'rgba(255, 180, 50, 0.85)', fontSize: '0.72rem', fontWeight: 500, marginBottom: '0.2rem' }}>
                      Spot description
                    </div>
                    <div style={{ color: '#fff', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{track.notes}</div>
                  </div>
                )}

                {/* Stations this spot flights on. Only present on batch
                    reviews initiated from a campaign; library-originated
                    reviews carry an empty array and this block hides. */}
                {Array.isArray(track.stations) && track.stations.length > 0 && (
                  <div style={{
                    marginTop: '0.6rem',
                    padding: '0.6rem 0.75rem',
                    backgroundColor: 'rgba(107, 107, 255, 0.06)',
                    border: '1px solid rgba(107, 107, 255, 0.2)',
                    borderRadius: '6px',
                  }}>
                    <div style={{ color: 'rgba(107, 107, 255, 0.85)', fontSize: '0.72rem', fontWeight: 500, marginBottom: '0.35rem' }}>
                      Flighting on {track.stations.length} station{track.stations.length === 1 ? '' : 's'}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                      {track.stations.map((s: any, i: number) => (
                        <span
                          key={`${s.flightingCode || s.station}-${i}`}
                          title={s.flightingCode ? `${s.station}${s.mediaOwner ? ` — ${s.mediaOwner}` : ''} · ${s.flightingCode}` : s.station}
                          style={{
                            padding: '2px 8px',
                            borderRadius: '3px',
                            fontSize: '0.72rem',
                            backgroundColor: 'rgba(255,255,255,0.06)',
                            color: '#fff',
                          }}
                        >
                          {s.station || '(unknown)'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Audio player */}
                {isActive && mp3Url && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <WavePlayer
                      id={`previewAudioPlayer-batch-${idx}`}
                      file={mp3Url}
                      noAutoplay
                      isActiveTrack={true}
                    />
                  </div>
                )}

                {/* Inline rejection reason form */}
                {rejectingTrackId === track.trackId && (
                  <div style={{
                    marginTop: '0.75rem', padding: '0.75rem',
                    backgroundColor: 'rgba(255, 80, 80, 0.05)',
                    border: '1px solid rgba(255, 80, 80, 0.15)',
                    borderRadius: '6px',
                  }}>
                    <Label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                      Reason for rejection
                    </Label>
                    <Input
                      type="textarea"
                      bsSize="sm"
                      rows={2}
                      value={rejectReasonText}
                      onChange={(e: any) => setRejectReasonText(e.target.value)}
                      placeholder="Please provide a reason for rejecting this spot..."
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        color: '#fff', fontSize: '0.85rem', resize: 'none',
                        marginBottom: '0.5rem',
                      }}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setRejectingTrackId(null)}
                        style={{
                          background: 'none', border: '1px solid rgba(255,255,255,0.15)',
                          color: 'rgba(255,255,255,0.5)', cursor: 'pointer',
                          fontSize: '0.75rem', padding: '3px 10px', borderRadius: '4px',
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          setBatchTrackDecisions(prev => ({ ...prev, [track.trackId]: { decision: 'rejected', reason: rejectReasonText } }));
                          setRejectingTrackId(null);
                          setRejectReasonText('');
                        }}
                        disabled={!rejectReasonText.trim()}
                        style={{
                          background: 'none', border: '1px solid rgba(255,80,80,0.4)',
                          color: rejectReasonText.trim() ? '#ff5050' : 'rgba(255,255,255,0.3)',
                          cursor: rejectReasonText.trim() ? 'pointer' : 'not-allowed',
                          fontSize: '0.75rem', padding: '3px 10px', borderRadius: '4px',
                        }}
                      >
                        Confirm Rejection
                      </button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          );
        })}

        {/* Submit bar */}
        <Card style={{ ...cardStyle, position: 'sticky', bottom: '1rem', zIndex: 10 }}>
          <CardBody style={{ padding: '0.75rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
              {Object.keys(batchTrackDecisions).length} of {tracks.length} reviewed
              {approvedCount > 0 && <span style={{ color: '#50c878', marginLeft: '8px' }}>{approvedCount} approved</span>}
              {rejectedCount > 0 && <span style={{ color: '#ff5050', marginLeft: '8px' }}>{rejectedCount} rejected</span>}
              {cannotAct && (
                <span style={{ color: 'rgba(255,180,50,0.85)', marginLeft: '10px' }}>
                  {orgRequired ? '· Select an organization above' : '· Enter your name above'}
                </span>
              )}
            </span>
            <Button
              className="btn-success-custom"
              onClick={handleBatchSubmit}
              disabled={batchSubmitting || Object.keys(batchTrackDecisions).length === 0 || cannotAct}
              size="sm"
            >
              <span style={{ position: 'relative', zIndex: 2 }}>
                {batchSubmitting ? (
                  <><Spinner size="sm" className="mr-1" /> Submitting...</>
                ) : (
                  <>Submit Review ({Object.keys(batchTrackDecisions).length})</>
                )}
              </span>
            </Button>
          </CardBody>
        </Card>
      </Container>
    );
  }

  // Successfully submitted (single track)
  if (submitted && decision) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md="6">
            <Card style={cardStyle}>
              <div style={headerStyle}>
                <h4 style={{ color: '#fff', margin: 0 }}>Review Submitted</h4>
              </div>
              <CardBody style={{ padding: '1.5rem', color: '#fff', textAlign: 'center' }}>
                <div style={{ marginBottom: '1rem' }}>
                  {decision === 'approved' ? (
                    <span style={{
                      display: 'inline-block',
                      padding: '8px 20px',
                      fontSize: '1rem',
                      borderRadius: '6px',
                      backgroundColor: 'rgba(80, 200, 120, 0.2)',
                      color: '#50c878',
                      fontWeight: '600',
                    }}>
                      Approved
                    </span>
                  ) : (
                    <span style={{
                      display: 'inline-block',
                      padding: '8px 20px',
                      fontSize: '1rem',
                      borderRadius: '6px',
                      backgroundColor: 'rgba(255, 80, 80, 0.2)',
                      color: '#ff5050',
                      fontWeight: '600',
                    }}>
                      Rejected
                    </span>
                  )}
                </div>
                <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: 0 }}>
                  Your review for "<strong>{reviewInfo?.review?.trackName}</strong>" has been submitted.
                  {decision === 'approved'
                    ? ' The studio has been notified of your approval.'
                    : ' The studio has been notified and can re-upload a corrected version.'
                  }
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Review interface
  const playbackUrl = getPlaybackUrl();
  const track = reviewInfo?.track;
  const review = reviewInfo?.review;

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md="8" lg="6">
          <Card style={cardStyle}>
            <div style={headerStyle}>
              <h4 style={{ color: '#fff', margin: 0 }}>Spot Review</h4>
              <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem', margin: '0.25rem 0 0 0' }}>
                From: {review?.sourceOrgName || reviewInfo?.sourceOrg?.name || 'Unknown'}
              </p>
            </div>
            <CardBody style={{ padding: '1.5rem', color: '#fff' }}>
              {/* Track info */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h5 style={{ color: 'var(--accent-1-color)', marginBottom: '0.5rem' }}>
                  {track?.trackName || review?.trackName || 'Unknown Spot'}
                </h5>

                {/* Metadata pills */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                  {track?.language && track.language.length > 0 && (
                    <span style={{
                      padding: '2px 10px',
                      fontSize: '0.8rem',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}>
                      {track.language.map(l => l.label).join(', ')}
                    </span>
                  )}
                  {track?.client && (
                    <span style={{
                      padding: '2px 10px',
                      fontSize: '0.8rem',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}>
                      Client: {track.client.label}
                    </span>
                  )}
                  {track?.agency && (
                    <span style={{
                      padding: '2px 10px',
                      fontSize: '0.8rem',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}>
                      Agency: {track.agency.label}
                    </span>
                  )}
                </div>
              </div>

              {/* Spot description (from audiometa.notes — usually originates
                  from the chase-list brief). Shown ABOVE the audio so the
                  reviewer reads the spec, then listens. */}
              {track?.notes && (
                <div style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  backgroundColor: 'rgba(255, 180, 50, 0.06)',
                  border: '1px solid rgba(255, 180, 50, 0.2)',
                  borderRadius: '8px',
                }}>
                  <Label style={{ color: 'rgba(255, 180, 50, 0.85)', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.35rem' }}>
                    Spot description
                  </Label>
                  <p style={{ color: '#fff', marginBottom: 0, whiteSpace: 'pre-wrap' }}>{track.notes}</p>
                </div>
              )}

              {/* Audio playback */}
              {playbackUrl ? (
                <div style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}>
                  <WavePlayer
                    id={`review-${reviewCode}`}
                    file={playbackUrl}
                    isActiveTrack={true}
                    noAutoplay={true}
                  />
                </div>
              ) : (
                <div style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  textAlign: 'center',
                  color: 'rgba(255, 255, 255, 0.5)',
                }}>
                  No audio available for preview
                </div>
              )}

              {/* Notes from studio */}
              {review?.notes && (
                <div style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}>
                  <Label style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', fontWeight: '500' }}>
                    Notes from studio
                  </Label>
                  <p style={{ color: '#fff', marginBottom: 0 }}>{review.notes}</p>
                </div>
              )}

              {/* Reviewer identity / auth section */}
              <div style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}>
                {!isAuthenticated ? (
                  <>
                    <Label style={{ color: '#fff', fontWeight: '500', marginBottom: '0.75rem', display: 'block' }}>
                      Enter your details to review
                    </Label>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '150px' }}>
                        <Label style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem' }}>
                          Name <span style={{ color: 'var(--accent-1-color)' }}>*</span>
                        </Label>
                        <Input
                          type="text"
                          value={reviewerName}
                          onChange={(e) => setReviewerName(e.target.value)}
                          placeholder="Your name"
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            border: `1px solid ${!reviewerName.trim() ? 'rgba(255, 255, 255, 0.2)' : 'rgba(80, 200, 120, 0.3)'}`,
                            color: '#fff',
                            borderRadius: '4px',
                          }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '150px' }}>
                        <Label style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem' }}>Email</Label>
                        <Input
                          type="email"
                          value={reviewerEmail}
                          onChange={(e) => setReviewerEmail(e.target.value)}
                          placeholder="Your email (optional)"
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: '#fff',
                            borderRadius: '4px',
                          }}
                        />
                      </div>
                    </div>

                    {/* OR divider */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      margin: '1rem 0',
                      gap: '1rem',
                    }}>
                      <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.15)' }} />
                      <span style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.8rem', fontWeight: '500' }}>OR</span>
                      <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.15)' }} />
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                    }}>
                      <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem' }}>
                        Have an account?
                      </span>
                      <Button
                        size="sm"
                        className="btn-primary-custom"
                        onClick={handleSignIn}
                        style={{ fontSize: '0.75rem', padding: '2px 10px' }}
                      >
                        <span style={{ position: 'relative', zIndex: 2 }}>Sign In</span>
                      </Button>
                      <Button
                        size="sm"
                        className="btn-info-custom"
                        onClick={handleSignUp}
                        style={{ fontSize: '0.75rem', padding: '2px 10px' }}
                      >
                        <span style={{ position: 'relative', zIndex: 2 }}>Create Account</span>
                      </Button>
                    </div>
                  </>
                ) : organizations.length === 0 ? (
                  <>
                    <Alert color="warning" style={{ marginBottom: '0.75rem' }}>
                      You need an organization to link this review to your account.
                    </Alert>
                    <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                      Create or join an organization, or continue reviewing without one.
                    </p>
                    <Button
                      size="sm"
                      className="btn-primary-custom"
                      onClick={handleCreateOrg}
                      style={{ fontSize: '0.8rem' }}
                    >
                      <span style={{ position: 'relative', zIndex: 2 }}>Create Organization</span>
                    </Button>
                  </>
                ) : !currentOrgId ? (
                  <>
                    <Alert color="info" style={{ marginBottom: '0.75rem' }}>
                      Select an organization to link this review.
                    </Alert>
                    <Button
                      size="sm"
                      className="btn-primary-custom"
                      onClick={handleSelectOrg}
                      style={{ fontSize: '0.8rem' }}
                    >
                      <span style={{ position: 'relative', zIndex: 2 }}>Select Organization</span>
                    </Button>
                  </>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.85rem' }}>Reviewing as: </span>
                      <span style={{ color: '#fff', fontWeight: '500' }}>{currentOrgName}</span>
                    </div>
                    {organizations.length > 1 && (
                      <Button
                        size="sm"
                        color="link"
                        onClick={handleSelectOrg}
                        style={{ color: 'var(--accent-1-color)', fontSize: '0.8rem', padding: 0 }}
                      >
                        Switch
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Error message */}
              {error && (
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: 'rgba(255, 80, 80, 0.1)',
                  borderRadius: '6px',
                  color: '#ff5050',
                  fontSize: '0.9rem',
                }}>
                  {error}
                </div>
              )}

              {/* Rejection reason (shown when reject is selected) */}
              {decision === 'rejected' && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <Label style={{ color: '#fff', fontWeight: '500' }}>Reason for rejection</Label>
                  <Input
                    type="textarea"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide a reason for rejection..."
                    rows={3}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: '#fff',
                      borderRadius: '4px',
                      resize: 'vertical',
                    }}
                  />
                </div>
              )}

              {/* Action buttons */}
              {(() => {
                const nameRequired = !isAuthenticated && !reviewerName.trim();
                const orgRequired = isAuthenticated && organizations.length > 0 && !currentOrgId;
                const cannotSubmit = nameRequired || orgRequired;
                return (
                <>
              {nameRequired && (
                <p style={{ color: 'rgba(255, 180, 50, 0.8)', fontSize: '0.85rem', textAlign: 'center', marginBottom: '0.5rem' }}>
                  Please enter your name above or sign in to approve or reject.
                </p>
              )}
              {orgRequired && (
                <p style={{ color: 'rgba(255, 180, 50, 0.8)', fontSize: '0.85rem', textAlign: 'center', marginBottom: '0.5rem' }}>
                  Please select an organization above to approve or reject.
                </p>
              )}
              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center',
                paddingTop: '0.5rem',
              }}>
                {decision === 'rejected' ? (
                  <>
                    <Button
                      className="btn-danger-custom"
                      onClick={handleSubmitReview}
                      disabled={submitting || cannotSubmit}
                      style={{ minWidth: '140px', ...(cannotSubmit ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                    >
                      <span style={{ position: 'relative', zIndex: 2 }}>
                        {submitting ? 'Submitting...' : 'Confirm Rejection'}
                      </span>
                    </Button>
                    <Button
                      className="btn-primary-custom"
                      onClick={() => setDecision(null)}
                      disabled={submitting}
                    >
                      <span style={{ position: 'relative', zIndex: 2 }}>Back</span>
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      className="btn-success-custom"
                      disabled={submitting || cannotSubmit}
                      style={{ minWidth: '140px', ...(cannotSubmit ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                      onClick={() => {
                        if (cannotSubmit) return;
                        setDecision('approved');
                        // Auto-submit approval (no reason needed)
                        setSubmitting(true);
                        if (firebase && reviewInfo) {
                          firebase.submitReview(
                            reviewInfo.orgId,
                            reviewInfo.trackId,
                            reviewInfo.reviewId,
                            'approved',
                            {
                              reviewerName: reviewerName || undefined,
                              reviewerEmail: reviewerEmail || undefined,
                              reviewedVia: 'external-link',
                              reviewerOrgId: currentOrgId || undefined,
                              reviewerOrgName: currentOrgName || undefined,
                            }
                          ).then((success: boolean) => {
                            if (success) {
                              setDecision('approved');
                              setSubmitted(true);
                            } else {
                              setError('Failed to submit approval');
                            }
                            setSubmitting(false);
                          }).catch(() => {
                            setError('Error submitting approval');
                            setSubmitting(false);
                          });
                        }
                      }}
                    >
                      <span style={{ position: 'relative', zIndex: 2 }}>
                        {submitting ? 'Submitting...' : 'Approve'}
                      </span>
                    </Button>
                    <Button
                      className="btn-danger-custom"
                      onClick={() => { if (!cannotSubmit) setDecision('rejected'); }}
                      disabled={submitting || cannotSubmit}
                      style={{ minWidth: '140px', ...(cannotSubmit ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                    >
                      <span style={{ position: 'relative', zIndex: 2 }}>Reject</span>
                    </Button>
                  </>
                )}
              </div>
              </>
                );
              })()}
            </CardBody>
          </Card>

          {/* Review code footer */}
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <small style={{ color: 'rgba(255, 255, 255, 0.3)' }}>
              Review code: {reviewCode}
            </small>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ReviewSpot;
